import { useState } from "react";
import type { ContentPiece } from "../../../../shared/types";
import { ContentCard } from "./ContentCard";
import { ContentEditWorkspace } from "./ContentEditWorkspace";

interface ContentReviewProps {
  pieces: ContentPiece[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: (id: string, content: string) => Promise<unknown> | void;
  onRegenerate: (id: string) => void;
  loading?: boolean;
}

export function ContentReview({
  pieces,
  onApprove,
  onReject,
  onEdit,
  onRegenerate,
  loading = false,
}: ContentReviewProps) {
  const [editingId, setEditingId] = useState<string | null>(null);

  const approvedCount = pieces.filter((p) => p.status === "approved").length;
  const totalCount = pieces.length;

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          <p className="text-sm text-slate-500">Loading content pieces...</p>
        </div>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center">
        <p className="text-sm text-slate-500">
          No content pieces yet. The pipeline is still generating content.
        </p>
      </div>
    );
  }

  const handleEditClick = (id: string) => {
    setEditingId(id);
  };

  const handleSave = async (id: string, content: string) => {
    // Close the workspace only after the write resolves; on failure it stays open
    // (edits intact) so the operator can retry instead of being told it saved.
    await onEdit(id, content);
    setEditingId(null);
  };

  const handleBackToReview = () => {
    setEditingId(null);
  };

  const editingPiece = editingId
    ? pieces.find((piece) => piece.id === editingId) ?? null
    : null;

  // Edit drawer: full content-draft workbench (edit + voice + facts + Ask AI to tighten)
  if (editingPiece) {
    return (
      <div className="-mx-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:-mx-8 lg:-mx-10">
        {/* TODO(PR3): pass requestedInspectorTab for external tab navigation (unused today). */}
        <ContentEditWorkspace
          piece={editingPiece}
          onSave={handleSave}
          onBackToReview={handleBackToReview}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Summary bar */}
      <div className="mb-6 rounded-lg bg-slate-50 px-4 py-3">
        <p className="text-sm font-medium text-slate-700">
          <span className="text-green-600">{approvedCount}</span> of{" "}
          <span className="font-semibold">{totalCount}</span> approved
        </p>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {pieces.map((piece) => (
          <ContentCard
            key={piece.id}
            piece={piece}
            onApprove={onApprove}
            onReject={onReject}
            onEdit={handleEditClick}
            onRegenerate={onRegenerate}
          />
        ))}
      </div>
    </div>
  );
}
